"use client";

import React from "react";
import { Calendar, User, ArrowRight, Tag, Clock, Share2 } from "lucide-react";
import { PostsProvider, usePosts, type Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";


const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="16721" className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} data-index={i} className="h-96 w-full animate-pulse rounded-sm bg-muted/50" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-md">
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden bg-slate-100">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          )}
          <CardHeader className="p-6 pb-3">
            <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
              <Calendar className="h-3.5 w-3.5 text-red-600" />
              <span>{formatDate(post.date)}</span>
            </div>
            <CardTitle className="line-clamp-2 text-xl font-bold leading-tight text-slate-900">
              <Link to={post.link || "#"} className="transition-colors hover:text-blue-600">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow px-6 py-0">
            <div 
              className="line-clamp-3 text-sm leading-relaxed text-slate-600"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="p-6 pt-4">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full justify-between group/btn border-slate-300 hover:bg-blue-600 hover:text-white">
                Read Full Guide
                <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsWrapper({ currentPostId, categoryIds }: { currentPostId: string; categoryIds: number[] }) {
  
  const relatedQueryArgs = {
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [parseInt(currentPostId) || 0],
    orderby: "date",
    order: "DESC",
    ...(categoryIds.length > 0 && { category__in: categoryIds }),
  };

  const wp_query = new WP_Query(relatedQueryArgs);

  return (
    <div className="mt-20 md:mt-32">
      <div className="mb-10 flex items-end justify-between border-b border-slate-200 pb-6">
        <div>
          <h3 className="text-2xl font-black uppercase tracking-tighter text-slate-900 md:text-3xl">More Electrical Insights</h3>
          <p className="mt-2 text-lg text-slate-600">Expert advice on safety, efficiency, and modern upgrades.</p>
        </div>
        <Link to="/BlogPage">
          <Button variant="ghost" className="hidden font-bold text-blue-600 hover:bg-blue-50 sm:flex">View All Articles</Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="mx-auto mb-8 h-8 w-32 animate-pulse rounded-full bg-muted/50" />
        <div className="mx-auto mb-6 h-16 w-3/4 animate-pulse rounded-sm bg-muted/50" />
        <div className="mx-auto mb-12 h-6 w-1/2 animate-pulse rounded-sm bg-muted/50" />
        <div className="mb-12 h-[500px] w-full animate-pulse rounded-sm bg-muted/50" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded-sm bg-muted/50" />
          <div className="h-4 w-full animate-pulse rounded-sm bg-muted/50" />
          <div className="h-4 w-2/3 animate-pulse rounded-sm bg-muted/50" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
        <div className="mb-6 rounded-full bg-red-100 p-4 text-red-600">
          <Clock className="h-10 w-10" />
        </div>
        <h2 className="mb-3 text-2xl font-bold text-slate-900">Unable to load article</h2>
        <p className="mb-8 max-w-md text-slate-600">We encountered an issue retrieving the content. Please refresh or contact our support team.</p>
        <Button onClick={refetch} size="lg" className="bg-blue-600 hover:bg-blue-700">Try Again</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
        <h2 className="mb-4 text-2xl font-bold text-slate-900">Article Not Found</h2>
        <p className="mb-8 text-slate-600">The specific guide you are looking for is not available at this time.</p>
        <Link to="/HomePage">
          <Button size="lg" className="bg-blue-600 hover:bg-blue-700">Return to Homepage</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const categoryIds = post.categories?.map(c => c.id) || [];

  return (
    <article className="container mx-auto max-w-5xl px-4 py-12 md:py-20">
      
      <header className="mx-auto mb-12 max-w-4xl text-center md:mb-16">
        <div className="mb-8 flex flex-wrap items-center justify-center gap-3">
          {post.categories?.map((cat, i) => (
            <span 
              key={i} 
              data-index={i} 
              className="rounded-sm bg-blue-600 px-4 py-1.5 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-red-600"
            >
              {cat.name}
            </span>
          ))}
        </div>
        
        <h1 className="mb-8 text-4xl font-black leading-tight tracking-tighter text-slate-900 md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-medium text-slate-500 md:gap-8">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2 border-slate-200 shadow-sm">
              <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name} />
              <AvatarFallback className="bg-slate-100 text-slate-400"><User className="h-5 w-5" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="font-bold text-slate-900">{post.author?.name || "Humberg Expert"}</p>
              <p className="text-xs uppercase tracking-wide text-red-600 font-bold">Lead Electrician</p>
            </div>
          </div>
          <Separator orientation="vertical" className="hidden h-8 bg-slate-200 md:block" />
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-600" />
            <span>{formatDate(post.date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-blue-600" />
            <span>Safety Guide</span>
          </div>
        </div>
      </header>

      
      {post.featuredImage && (
        <div className="mb-16 overflow-hidden rounded-sm shadow-2xl ring-1 ring-slate-200 md:mb-20">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      
      <div className="mx-auto max-w-3xl">
        <div 
          className="prose prose-lg prose-slate dark:prose-invert max-w-none 
            [&_p]:mb-6 [&_p]:leading-relaxed [&_p]:text-slate-700
            [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:text-3xl [&_h2]:font-black [&_h2]:tracking-tight [&_h2]:text-slate-900
            [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:text-2xl [&_h3]:font-bold [&_h3]:text-slate-900
            [&_ul]:mb-8 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-2 [&_li]:text-slate-700
            [&_ol]:mb-8 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-2
            [&_blockquote]:my-8 [&_blockquote]:border-l-4 [&_blockquote]:border-red-600 [&_blockquote]:bg-slate-50 [&_blockquote]:p-6 [&_blockquote]:italic [&_blockquote]:text-slate-900 [&_blockquote]:font-medium
            [&_img]:my-10 [&_img]:rounded-sm [&_img]:shadow-xl
            [&_a]:font-bold [&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-red-600"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        
        <div className="mt-16 flex flex-col gap-8 border-t border-slate-200 pt-8 md:flex-row md:items-center md:justify-between">
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="mr-2 h-4 w-4 text-slate-400" />
              {post.tags.map((tag, i) => (
                <Link key={i} data-index={i} to={tag.link || "#"}>
                  <span className="rounded-sm bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 transition-colors hover:bg-blue-600 hover:text-white">
                    #{tag.name}
                  </span>
                </Link>
              ))}
            </div>
          )}
          
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold uppercase tracking-wider text-slate-500">Share This Guide:</span>
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm border-slate-200 hover:text-blue-600">
              <Share2 className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm border-slate-200 hover:text-blue-600">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" /></svg>
            </Button>
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm border-slate-200 hover:text-blue-600">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" /></svg>
            </Button>
          </div>
        </div>

        
        <div className="mt-12 rounded-sm border border-slate-200 bg-slate-50 p-8 md:p-10 shadow-sm">
          <div className="flex flex-col items-center gap-8 text-center sm:flex-row sm:text-left">
            <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
              <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name} />
              <AvatarFallback className="bg-blue-600 text-white text-2xl"><User className="h-10 w-10" /></AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="mb-2 flex flex-col items-center gap-2 sm:flex-row sm:items-baseline">
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">{post.author?.name || "Humberg Electrical Services"}</h3>
                <span className="text-sm font-bold text-red-600 uppercase tracking-wide">Expert Contractor</span>
              </div>
              <p className="mb-6 text-base leading-relaxed text-slate-600">
                Proudly serving Richmond, Sugar Land, and Missouri City since 2013. Our family-owned team handles everything from complex home rewiring to 24/7 emergency calls. No job is too big or too small.
              </p>
              <Link to={post.author?.link || "#"}>
                <Button variant="outline" size="sm" className="gap-2 border-blue-600 text-blue-600 font-bold hover:bg-blue-600 hover:text-white">
                  Learn More About Us <ArrowRight className="h-3 w-3" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      
      <Separator className="my-16 bg-slate-200 md:my-24" />
      <RelatedPostsWrapper currentPostId={post.id} categoryIds={categoryIds} />
    </article>
  );
}



export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = typeof window !== 'undefined' 
    ? (window as any).wvcClient?.getCurrentWPQueryParams() || { post_type: "post" }
    : { post_type: "post" };
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-white text-slate-900">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}